import { useState, useEffect, useCallback } from "react";
import { 
  Mail, Send, Copy, Check, Loader2, Sparkles, 
  CheckCircle, AlertTriangle, XCircle, FileText,
  RefreshCw, Clock, Shield, FileCode, Zap, ChevronDown, ChevronUp, Bug,
  Pause, Play
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Progress } from "@/components/ui/progress";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";

interface AnalysisResult {
  overallScore: number;
  overallVerdict: "excellent" | "good" | "needs_improvement" | "poor";
  summary: string;
  authentication: {
    spf: { result: string; details: string };
    dkim: { result: string; details: string };
    dmarc: { result: string; details: string };
    score: number;
  };
  content: {
    spamScore: number;
    triggers: string[];
    positives: string[];
    score: number;
  };
  technical: {
    htmlQuality: string;
    hasPlainText: boolean;
    imageToTextRatio: string;
    linkCount: number;
    issues: string[];
    score: number;
  };
  recommendations: Array<{
    priority: "high" | "medium" | "low";
    category: string;
    issue: string;
    fix: string;
  }>;
  details: {
    from: string;
    subject: string;
    size: string;
  };
}

interface TestSession {
  testCode: string;
  testEmail: string;
  expiresAt: string;
  status: "pending" | "received" | "analyzed" | "expired";
}

interface WebhookHit {
  id: string;
  testCode: string;
  status: string;
  createdAt: string;
  receivedAt: string | null;
  fromAddress: string | null;
  subject: string | null;
}

export const EmailChecker = () => {
  const [activeTab, setActiveTab] = useState<"send" | "paste">("send");
  const [testSession, setTestSession] = useState<TestSession | null>(null);
  const [isCreatingTest, setIsCreatingTest] = useState(false);
  const [isPolling, setIsPolling] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [emailReceived, setEmailReceived] = useState(false);
  const [result, setResult] = useState<AnalysisResult | null>(null);
  
  // Debug panel state
  const [debugOpen, setDebugOpen] = useState(false);
  const [webhookHits, setWebhookHits] = useState<WebhookHit[]>([]);
  const [isLoadingHits, setIsLoadingHits] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [copied, setCopied] = useState(false);
  
  // Paste mode states
  const [pastedHeaders, setPastedHeaders] = useState("");

  const createTestSession = async () => {
    setIsCreatingTest(true);
    setResult(null);
    setEmailReceived(false);

    try {
      const { data, error } = await supabase.functions.invoke("create-email-test");
      
      if (error) throw error;
      if (data.error) throw new Error(data.error);

      setTestSession({
        testCode: data.testCode,
        testEmail: data.testEmail,
        expiresAt: data.expiresAt,
        status: "pending",
      });

      toast.success("Test session created! Send your email now.");
    } catch (error) {
      console.error("Error creating test:", error);
      toast.error("Failed to create test session");
    } finally {
      setIsCreatingTest(false);
    }
  };

  const copyEmail = async () => {
    if (!testSession?.testEmail) return;
    
    try {
      await navigator.clipboard.writeText(testSession.testEmail);
      setCopied(true);
      toast.success("Test email copied!");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Failed to copy");
    }
  };

  const checkForEmail = useCallback(async () => {
    if (!testSession?.testCode) return;

    try {
      const { data, error } = await supabase.functions.invoke("check-email-test", {
        body: { testCode: testSession.testCode },
      });

      if (error) throw error;

      if (data.status === "received" || data.status === "analyzed") {
        setEmailReceived(true);
        setIsPolling(false);
        
        if (data.status === "analyzed" && data.analysisResult) {
          setResult(data.analysisResult);
        }
        
        return true;
      }
      
      if (data.status === "expired") {
        setTestSession(prev => prev ? { ...prev, status: "expired" } : null);
        setIsPolling(false);
        return true;
      }

      return false;
    } catch (error) {
      console.error("Error checking for email:", error);
      return false;
    }
  }, [testSession?.testCode]);

  // Polling effect
  useEffect(() => {
    if (!isPolling || !testSession?.testCode) return;

    const interval = setInterval(async () => {
      const received = await checkForEmail();
      if (received) {
        clearInterval(interval);
      }
    }, 3000);

    return () => clearInterval(interval);
  }, [isPolling, testSession?.testCode, checkForEmail]);

  const startPolling = () => {
    setIsPolling(true);
    toast.info("Waiting for your email...");
  };

  const analyzeReceivedEmail = async () => {
    if (!testSession?.testCode) return;

    setIsAnalyzing(true);

    try {
      const { data, error } = await supabase.functions.invoke("analyze-test-email", {
        body: { testCode: testSession.testCode },
      });

      if (error) throw error;
      if (data.error) throw new Error(data.error);

      setResult(data);
      toast.success("Analysis complete!");
    } catch (error) {
      console.error("Error analyzing email:", error);
      toast.error("Failed to analyze email");
    } finally {
      setIsAnalyzing(false);
    }
  };

  const analyzeFromPaste = async () => {
    if (!pastedHeaders.trim()) {
      toast.error("Please paste email headers to analyze");
      return;
    }

    setIsAnalyzing(true);
    setResult(null);

    try {
      const { data, error } = await supabase.functions.invoke("analyze-email-headers", {
        body: { headers: pastedHeaders.trim() },
      });

      if (error) throw error;
      if (data.error) throw new Error(data.error);

      // Transform header analysis to match our result format
      const transformedResult: AnalysisResult = {
        overallScore: calculateOverallScore(data),
        overallVerdict: getVerdict(calculateOverallScore(data)),
        summary: data.summary,
        authentication: {
          spf: data.authentication?.spf || { result: "none", details: "Not found" },
          dkim: data.authentication?.dkim || { result: "none", details: "Not found" },
          dmarc: data.authentication?.dmarc || { result: "none", details: "Not found" },
          score: calculateAuthScore(data.authentication),
        },
        content: {
          spamScore: data.spamAnalysis?.score ? parseInt(data.spamAnalysis.score) : 50,
          triggers: data.spamAnalysis?.flags || [],
          positives: [],
          score: data.spamAnalysis?.verdict === "ham" ? 90 : 50,
        },
        technical: {
          htmlQuality: "unknown",
          hasPlainText: false,
          imageToTextRatio: "unknown",
          linkCount: 0,
          issues: data.issues?.map((i: any) => i.issue) || [],
          score: 70,
        },
        recommendations: data.issues?.map((i: any) => ({
          priority: i.severity as "high" | "medium" | "low",
          category: "general",
          issue: i.issue,
          fix: i.recommendation,
        })) || [],
        details: {
          from: data.sender?.from || "Unknown",
          subject: "From headers only",
          size: "N/A",
        },
      };

      setResult(transformedResult);
    } catch (error) {
      console.error("Error analyzing headers:", error);
      toast.error("Failed to analyze headers");
    } finally {
      setIsAnalyzing(false);
    }
  };

  const calculateAuthScore = (auth: any) => {
    if (!auth) return 0;
    let score = 0;
    if (auth.spf?.result === "pass") score += 33;
    if (auth.dkim?.result === "pass") score += 34;
    if (auth.dmarc?.result === "pass") score += 33;
    return score;
  };

  const calculateOverallScore = (data: any) => {
    const authScore = calculateAuthScore(data.authentication);
    const spamPenalty = data.issues?.length > 0 ? data.issues.length * 5 : 0;
    return Math.max(0, Math.min(100, authScore - spamPenalty));
  };

  const getVerdict = (score: number): "excellent" | "good" | "needs_improvement" | "poor" => {
    if (score >= 90) return "excellent";
    if (score >= 70) return "good";
    if (score >= 50) return "needs_improvement";
    return "poor";
  };

  const resetTest = () => {
    setTestSession(null);
    setResult(null);
    setEmailReceived(false);
    setIsPolling(false);
    setPastedHeaders("");
  };

  const fetchWebhookHits = async () => {
    setIsLoadingHits(true);
    try {
      const { data, error } = await supabase
        .from("email_test_sessions")
        .select("id, test_code, status, created_at, received_at, from_address, subject")
        .order("created_at", { ascending: false })
        .limit(10);

      if (error) throw error;

      setWebhookHits(
        (data || []).map((row) => ({
          id: row.id,
          testCode: row.test_code,
          status: row.status,
          createdAt: row.created_at,
          receivedAt: row.received_at,
          fromAddress: row.from_address,
          subject: row.subject,
        }))
      );
    } catch (error) {
      console.error("Error fetching webhook hits:", error);
      toast.error("Failed to load webhook history");
    } finally {
      setIsLoadingHits(false);
    }
  };

  // Initial fetch when debug panel opens
  useEffect(() => {
    if (debugOpen) {
      fetchWebhookHits();
    }
  }, [debugOpen]);

  // Auto-refresh every 3 seconds when debug panel is open and auto-refresh is enabled
  useEffect(() => {
    if (!debugOpen || !autoRefresh) return;

    const interval = setInterval(() => {
      fetchWebhookHits();
    }, 3000);

    return () => clearInterval(interval);
  }, [debugOpen, autoRefresh]);

  const formatDeliveryTime = (createdAt: string, receivedAt: string) => {
    const created = new Date(createdAt).getTime();
    const received = new Date(receivedAt).getTime();
    const diffMs = received - created;
    
    if (diffMs < 0) return "instant";
    
    const seconds = Math.floor(diffMs / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    
    if (hours > 0) {
      return `${hours}h ${minutes % 60}m ${seconds % 60}s`;
    }
    if (minutes > 0) {
      return `${minutes}m ${seconds % 60}s`;
    }
    return `${seconds}s`;
  };

  const getScoreColor = (score: number) => {
    if (score >= 90) return "text-success";
    if (score >= 70) return "text-yellow-500";
    if (score >= 50) return "text-warning";
    return "text-destructive";
  };

  const getScoreBg = (score: number) => {
    if (score >= 90) return "bg-success";
    if (score >= 70) return "bg-yellow-500";
    if (score >= 50) return "bg-warning";
    return "bg-destructive";
  };

  const getVerdictLabel = (verdict: string) => {
    switch (verdict) {
      case "excellent": return "Excellent";
      case "good": return "Good";
      case "needs_improvement": return "Needs Work";
      case "poor": return "Poor";
      default: return verdict;
    }
  };

  const getAuthIcon = (result: string) => {
    if (result === "pass") return <CheckCircle className="w-4 h-4 text-success" />;
    if (result === "fail") return <XCircle className="w-4 h-4 text-destructive" />;
    return <AlertTriangle className="w-4 h-4 text-warning" />;
  };

  const getAuthBg = (result: string) => {
    if (result === "pass") return "bg-success/10 border-success/20";
    if (result === "fail") return "bg-destructive/10 border-destructive/20";
    return "bg-warning/10 border-warning/20";
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "high": return "bg-destructive/10 text-destructive border-destructive/20";
      case "medium": return "bg-warning/10 text-warning border-warning/20";
      case "low": return "bg-blue-500/10 text-blue-500 border-blue-500/20";
      default: return "bg-muted text-muted-foreground";
    }
  };

  return (
    <div className="section-card">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
          <Mail className="w-5 h-5 text-primary" />
        </div>
        <div>
          <h3 className="font-display text-lg font-semibold text-foreground">
            Email Deliverability Checker
          </h3>
          <p className="text-sm text-muted-foreground">
            Test your actual emails for deliverability issues
          </p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as "send" | "paste")} className="space-y-4">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="send" className="flex items-center gap-2">
            <Send className="w-4 h-4" />
            Send Test Email
          </TabsTrigger>
          <TabsTrigger value="paste" className="flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Paste Headers
          </TabsTrigger>
        </TabsList>

        <TabsContent value="send" className="space-y-4">
          {!testSession && !result && (
            <div className="text-center py-8">
              <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <Send className="w-8 h-8 text-primary" />
              </div>
              <h4 className="font-semibold text-foreground mb-2">
                Send a Real Test Email
              </h4>
              <p className="text-sm text-muted-foreground mb-6 max-w-md mx-auto">
                Get a unique test email address. Send an email from your actual sending system and get a comprehensive deliverability report.
              </p>
              <Button onClick={createTestSession} disabled={isCreatingTest}>
                {isCreatingTest ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Generate Test Email
                  </>
                )}
              </Button>
            </div>
          )}

          {testSession && !emailReceived && !result && (
            <div className="space-y-4">
              <div className="bg-primary/5 border border-primary/20 rounded-xl p-4">
                <p className="text-sm font-medium text-foreground mb-3">
                  Send your test email to:
                </p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 code-block">
                    {testSession.testEmail}
                  </code>
                  <Button variant="outline" size="icon" onClick={copyEmail}>
                    {copied ? (
                      <Check className="w-4 h-4 text-success" />
                    ) : (
                      <Copy className="w-4 h-4" />
                    )}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  <Clock className="w-3 h-3 inline mr-1" />
                  Expires in 24 hours
                </p>
              </div>

              {!isPolling ? (
                <Button onClick={startPolling} className="w-full">
                  <RefreshCw className="w-4 h-4 mr-2" />
                  I've Sent the Email - Check Now
                </Button>
              ) : (
                <div className="text-center py-4">
                  <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-3" />
                  <p className="text-sm text-muted-foreground">
                    Waiting for your email...
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    This will automatically update when received
                  </p>
                </div>
              )}

              <Button variant="ghost" onClick={resetTest} className="w-full text-muted-foreground">
                Cancel & Start Over
              </Button>
            </div>
          )}

          {emailReceived && !result && (
            <div className="space-y-4">
              <div className="bg-success/10 border border-success/20 rounded-xl p-4 text-center">
                <CheckCircle className="w-8 h-8 text-success mx-auto mb-2" />
                <p className="text-sm font-medium text-foreground">
                  Email Received!
                </p>
                <p className="text-xs text-muted-foreground">
                  Ready to analyze your email for deliverability issues
                </p>
              </div>
              
              <Button onClick={analyzeReceivedEmail} disabled={isAnalyzing} className="w-full">
                {isAnalyzing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Analyzing...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Analyze Email
                  </>
                )}
              </Button>
            </div>
          )}
        </TabsContent>

        <TabsContent value="paste" className="space-y-4">
          {!result && (
            <>
              <div>
                <label className="text-sm font-medium text-foreground mb-2 block">
                  Raw Email Headers
                </label>
                <Textarea
                  value={pastedHeaders}
                  onChange={(e) => setPastedHeaders(e.target.value)}
                  placeholder="Paste your email headers here (View Original in Gmail, or View Source in other clients)..."
                  className="min-h-[150px] font-mono text-xs resize-none"
                />
                <p className="text-xs text-muted-foreground mt-2">
                  In Gmail: Open email → Three dots → "Show original" → Copy headers
                </p>
              </div>
              
              <Button 
                onClick={analyzeFromPaste} 
                disabled={isAnalyzing || !pastedHeaders.trim()}
                className="w-full"
              >
                {isAnalyzing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Analyzing...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Analyze Headers
                  </>
                )}
              </Button>
            </>
          )}
        </TabsContent>
      </Tabs>

      {/* Results Section */}
      {result && (
        <div className="space-y-4 pt-4 border-t border-border mt-4">
          {/* Overall Score */}
          <div className="bg-muted/50 rounded-xl p-4 text-center">
            <div className="flex items-center justify-center gap-4 mb-3">
              <div className={`text-5xl font-bold ${getScoreColor(result.overallScore)}`}>
                {result.overallScore}
              </div>
              <div className="text-left">
                <p className="text-sm text-muted-foreground">Overall Score</p>
                <p className={`font-semibold ${getScoreColor(result.overallScore)}`}>
                  {getVerdictLabel(result.overallVerdict)}
                </p>
              </div>
            </div>
            <Progress 
              value={result.overallScore} 
              className="h-2"
            />
            <p className="text-sm text-muted-foreground mt-3">
              {result.summary}
            </p>
          </div>

          {/* Score Breakdown */}
          <div className="grid sm:grid-cols-3 gap-3">
            <div className="bg-muted/30 rounded-xl p-3 text-center">
              <Shield className="w-5 h-5 text-primary mx-auto mb-1" />
              <p className="text-xs text-muted-foreground">Authentication</p>
              <p className={`text-lg font-bold ${getScoreColor(result.authentication.score)}`}>
                {result.authentication.score}%
              </p>
            </div>
            <div className="bg-muted/30 rounded-xl p-3 text-center">
              <FileCode className="w-5 h-5 text-primary mx-auto mb-1" />
              <p className="text-xs text-muted-foreground">Content</p>
              <p className={`text-lg font-bold ${getScoreColor(result.content.score)}`}>
                {result.content.score}%
              </p>
            </div>
            <div className="bg-muted/30 rounded-xl p-3 text-center">
              <Zap className="w-5 h-5 text-primary mx-auto mb-1" />
              <p className="text-xs text-muted-foreground">Technical</p>
              <p className={`text-lg font-bold ${getScoreColor(result.technical.score)}`}>
                {result.technical.score}%
              </p>
            </div>
          </div>

          {/* Authentication Results */}
          <div>
            <h4 className="text-sm font-medium text-foreground mb-3 flex items-center gap-2">
              <Shield className="w-4 h-4 text-primary" />
              Authentication Results
            </h4>
            <div className="grid sm:grid-cols-3 gap-3">
              {["spf", "dkim", "dmarc"].map((auth) => {
                const authData = result.authentication[auth as keyof typeof result.authentication];
                if (typeof authData !== "object") return null;
                return (
                  <div key={auth} className={`rounded-xl border p-3 ${getAuthBg(authData.result)}`}>
                    <div className="flex items-center gap-2 mb-1">
                      {getAuthIcon(authData.result)}
                      <span className="font-semibold text-foreground uppercase text-sm">{auth}</span>
                      <span className={`ml-auto text-xs font-medium uppercase ${
                        authData.result === "pass" ? "text-success" :
                        authData.result === "fail" ? "text-destructive" : "text-warning"
                      }`}>
                        {authData.result}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground">{authData.details}</p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Recommendations */}
          {result.recommendations.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-foreground mb-3 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-warning" />
                Recommendations ({result.recommendations.length})
              </h4>
              <div className="space-y-2">
                {result.recommendations.map((rec, i) => (
                  <div key={i} className={`rounded-xl border p-3 ${getPriorityColor(rec.priority)}`}>
                    <div className="flex items-start gap-2">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium uppercase ${getPriorityColor(rec.priority)}`}>
                        {rec.priority}
                      </span>
                      <div>
                        <p className="text-sm font-medium text-foreground">{rec.issue}</p>
                        <p className="text-xs text-muted-foreground mt-1">💡 {rec.fix}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Content Analysis */}
          {result.content.triggers.length > 0 && (
            <div className="bg-warning/10 border border-warning/20 rounded-xl p-3">
              <h4 className="text-sm font-medium text-foreground mb-2">⚠️ Spam Triggers Found</h4>
              <div className="flex flex-wrap gap-1">
                {result.content.triggers.map((trigger, i) => (
                  <span key={i} className="px-2 py-0.5 rounded bg-warning/20 text-xs text-warning">
                    {trigger}
                  </span>
                ))}
              </div>
            </div>
          )}

          {result.content.positives.length > 0 && (
            <div className="bg-success/10 border border-success/20 rounded-xl p-3">
              <h4 className="text-sm font-medium text-foreground mb-2">✓ Good Practices</h4>
              <div className="flex flex-wrap gap-1">
                {result.content.positives.map((positive, i) => (
                  <span key={i} className="px-2 py-0.5 rounded bg-success/20 text-xs text-success">
                    {positive}
                  </span>
                ))}
              </div>
            </div>
          )}

          <Button variant="outline" onClick={resetTest} className="w-full">
            <RefreshCw className="w-4 h-4 mr-2" />
            Run Another Test
          </Button>
        </div>
      )}

      {/* Debug Panel */}
      <Collapsible open={debugOpen} onOpenChange={setDebugOpen} className="mt-6 border-t border-border pt-4">
        <CollapsibleTrigger asChild>
          <Button variant="ghost" className="w-full flex items-center justify-between text-muted-foreground hover:text-foreground">
            <span className="flex items-center gap-2">
              <Bug className="w-4 h-4" />
              Webhook Debug Panel
            </span>
            {debugOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent className="mt-3">
          <div className="bg-muted/30 rounded-xl p-4 space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-medium text-foreground">Last 10 Test Sessions</h4>
              <div className="flex items-center gap-2">
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => setAutoRefresh(!autoRefresh)}
                  className={autoRefresh ? "text-success" : "text-muted-foreground"}
                  title={autoRefresh ? "Auto-refresh ON (3s)" : "Auto-refresh OFF"}
                >
                  {autoRefresh ? (
                    <Pause className="w-3 h-3" />
                  ) : (
                    <Play className="w-3 h-3" />
                  )}
                </Button>
                <Button variant="ghost" size="sm" onClick={fetchWebhookHits} disabled={isLoadingHits}>
                  {isLoadingHits ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : (
                    <RefreshCw className="w-3 h-3" />
                  )}
                </Button>
              </div>
            </div>

            {/* Auto-refresh indicator */}
            {autoRefresh && (
              <div className="flex items-center gap-2 text-[10px] text-success">
                <span className="w-1.5 h-1.5 rounded-full bg-success animate-pulse" />
                Auto-refreshing every 3 seconds
              </div>
            )}

            {/* Wrong address warning */}
            <div className="bg-warning/10 border border-warning/20 rounded-lg p-3">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-warning flex-shrink-0 mt-0.5" />
                <div className="text-xs">
                  <p className="font-medium text-warning mb-1">Important: Send to the correct address</p>
                  <p className="text-muted-foreground">
                    You must send your test email to <code className="bg-muted px-1 rounded">test-{'<code>'}@inbound.itdukes.com</code>
                  </p>
                  <p className="text-muted-foreground mt-1">
                    <span className="text-destructive">✗ Wrong:</span>{" "}
                    <code className="bg-destructive/10 px-1 rounded text-destructive">...@inbound.postmarkapp.com</code>
                  </p>
                  <p className="text-muted-foreground">
                    <span className="text-success">✓ Correct:</span>{" "}
                    <code className="bg-success/10 px-1 rounded text-success">test-abc123@inbound.itdukes.com</code>
                  </p>
                </div>
              </div>
            </div>
            
            {webhookHits.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">
                No test sessions found
              </p>
            ) : (
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {webhookHits.map((hit) => (
                  <div 
                    key={hit.id} 
                    className={`rounded-lg border p-3 text-xs ${
                      hit.status === "received" || hit.status === "analyzed"
                        ? "bg-success/10 border-success/20"
                        : hit.status === "pending"
                        ? "bg-muted border-border"
                        : "bg-destructive/10 border-destructive/20"
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <code className="font-mono text-foreground">
                        test-{hit.testCode}@...
                      </code>
                      <span className={`px-2 py-0.5 rounded text-[10px] font-medium uppercase ${
                        hit.status === "received" || hit.status === "analyzed"
                          ? "bg-success/20 text-success"
                          : hit.status === "pending"
                          ? "bg-muted-foreground/20 text-muted-foreground"
                          : "bg-destructive/20 text-destructive"
                      }`}>
                        {hit.status}
                      </span>
                    </div>
                    <div className="text-muted-foreground space-y-0.5">
                      <p>Created: {new Date(hit.createdAt).toLocaleString()}</p>
                      {hit.receivedAt && (
                        <>
                          <p className="text-success">Received: {new Date(hit.receivedAt).toLocaleString()}</p>
                          <p className="text-primary font-medium">
                            ⏱ Delivery time: {formatDeliveryTime(hit.createdAt, hit.receivedAt)}
                          </p>
                        </>
                      )}
                      {hit.fromAddress && <p>From: {hit.fromAddress}</p>}
                      {hit.subject && <p>Subject: {hit.subject}</p>}
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            <p className="text-[10px] text-muted-foreground text-center">
              If emails are sent but status remains "pending", the webhook isn't receiving data from Postmark.
            </p>
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
};
